/*
 * Copyright (c) 2018 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
import winston from 'winston';
import { CloudwatchLogFormat } from './cloudwatch-log-format';
import format from 'date-fns/format';
import { BasicLogFormat } from './basic-log-format';
import { SLACK_WEBHOOK_URL } from './constants';
import { LOG_LEVELS } from './log-levels';
const { v4: uuidv4 } = require('uuid');
const CloudWatchTransport = require('winston-aws-cloudwatch');
const SlackHook = require('winston-slack-webhook-transport');
const DEFAULT_CLOUDWATCH_BATCH_SIZE = 200;
const CLOUDWATCH_BATCH_SIZE = Number(process.env.CLOUDWATCH_BATCH_SIZE || DEFAULT_CLOUDWATCH_BATCH_SIZE);
export class LogFactory {
    constructor() {
        this._env = process.env.STAGE || process.env.NODE_ENV || 'dev';
        this._awsAccessKeyId = process.env.AWS_ACCESS_KEY_ID;
        this._awsSecret = process.env.AWS_SECRET_ACCESS_KEY;
        this._awsRegion = process.env.AWS_DEFAULT_REGION || 'eu-west-1';
        this._level = process.env.LOG_LEVEL || 'debug';
        this._transports = [];
        this.getAWSConfig = () => {
            if (this._awsAccessKeyId && this._awsSecret) {
                return {
                    accessKeyId: this._awsAccessKeyId,
                    secretAccessKey: this._awsSecret,
                    region: this._awsRegion,
                };
            }
            return {
                region: this._awsRegion,
            };
        };
        this.__getSlackLogger = (webhook) => {
            return new SlackHook({
                webhookUrl: webhook,
                level: 'slack',
            });
        };
        this.__getConsoleLogger = () => {
            return new winston.transports.Console({
                level: this._level,
            });
        };
        this.__getCloudWatchLogger = (groupName, streamName) => {
            if (this.isServer()) {
                this._transports.push(new CloudWatchTransport({
                    logGroupName: groupName,
                    logStreamName: streamName,
                    createLogGroup: true,
                    createLogStream: true,
                    submissionInterval: 2000,
                    submissionRetryCount: 1,
                    batchSize: CLOUDWATCH_BATCH_SIZE,
                    awsConfig: this.getAWSConfig(),
                }));
            }
        };
    }
    __hasCloudWatch() {
        return !!process.env.CLOUDWATCH_LOGS;
    }
    __cleanStr(str) {
        return str
            .toLowerCase()
            .replace(/[^a-z0-9-]/gi, '')
            .replace(`vamstar`, '');
    }
    __getLogGroupName(logGroup) {
        const pkgName = process.env.npm_package_name;
        let groupName;
        let baseName = '';
        if (pkgName) {
            baseName = `${this.__cleanStr(pkgName)}`;
        }
        groupName = `${this._env}/${baseName}`;
        if (logGroup) {
            groupName = `${groupName}-${logGroup}`;
        }
        return groupName;
    }
    __getStreamName(name) {
        let streamName = `${format(new Date(), 'MM/dd/yyy')}-${uuidv4()}`;
        if (name) {
            streamName = this.__cleanStr(name);
        }
        if (process.env.CLOUDWATCH_LOGS_STREAM_PER_THREAD) {
            streamName = uuidv4();
        }
        return streamName;
    }
    __getFilepath(groupName, streamName, suffix) {
        let filepath = `/tmp/${groupName.replace('/', '-')}`;
        if (streamName) {
            filepath = `${filepath}-${streamName.replace('/', '-')}`;
        }
        return `${filepath}.${suffix || 'debug'}.log`;
    }
    isServer() {
        return !(typeof window != 'undefined' && window.document);
    }
    __getFileTransport(groupName, streamName) {
        if (this.isServer()) {
            this._transports.push(new winston.transports.File({
                filename: this.__getFilepath(groupName, streamName),
                maxsize: 1048576,
                handleExceptions: true,
            }));
        }
    }
    __getExceptionTransport(groupName, streamName) {
        if (this.isServer()) {
            return [
                new winston.transports.File({
                    filename: this.__getFilepath(groupName, streamName, 'errors'),
                    maxsize: 1048576,
                }),
            ];
        }
        return undefined;
    }
    __getFormatOptions() {
        return {
            format: winston.format.combine(winston.format.json(), new BasicLogFormat({
                tsFileInfo: true,
                prettyPrint: true,
            }), winston.format.cli()),
        };
    }
    create(logStream, logGroup) {
        const groupName = this.__getLogGroupName(logGroup);
        const streamName = this.__getStreamName(logStream);
        const logKey = `${groupName}/${streamName}`;
        let logger = LogFactory.loggers[logKey];
        if (logger) {
            return logger;
        }
        if (SLACK_WEBHOOK_URL) {
            this._transports.push(this.__getSlackLogger(SLACK_WEBHOOK_URL));
        }
        if (this.__hasCloudWatch()) {
            this.__getCloudWatchLogger(groupName, streamName);
            logger = winston.createLogger({
                level: this._level,
                format: new CloudwatchLogFormat({
                    prettyPrint: false,
                }),
                transports: this._transports,
            });
        }
        else {
            this._transports.push(this.__getConsoleLogger());
            this.__getFileTransport(groupName, streamName);
            logger = winston.createLogger({
                levels: LOG_LEVELS,
                level: this._level,
                ...this.__getFormatOptions(),
                transports: this._transports,
                exceptionHandlers: this.__getExceptionTransport(groupName, streamName),
            });
        }
        LogFactory.loggers[logKey] = logger;
        return logger;
    }
}
LogFactory.loggers = {};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoibG9nLWZhY3RvcnkuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvbGliL2xvZy1mYWN0b3J5LnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOzs7Ozs7R0FNRztBQUVILE9BQU8sT0FBTyxNQUFNLFNBQVMsQ0FBQztBQUM5QixPQUFPLEVBQUUsbUJBQW1CLEVBQUUsTUFBTSw0QkFBNEIsQ0FBQztBQUNqRSxPQUFPLE1BQU0sTUFBTSxpQkFBaUIsQ0FBQztBQUVyQyxPQUFPLEVBQUUsY0FBYyxFQUFFLE1BQU0sb0JBQW9CLENBQUM7QUFDcEQsT0FBTyxFQUFFLGlCQUFpQixFQUFFLE1BQU0sYUFBYSxDQUFDO0FBQ2hELE9BQU8sRUFBRSxVQUFVLEVBQUUsTUFBTSxjQUFjLENBQUM7QUFFMUMsTUFBTSxFQUFFLEVBQUUsRUFBRSxNQUFNLEVBQUUsR0FBRyxPQUFPLENBQUMsTUFBTSxDQUFDLENBQUM7QUFDdkMsTUFBTSxtQkFBbUIsR0FBRyxPQUFPLENBQUMsd0JBQXdCLENBQUMsQ0FBQztBQUM5RCxNQUFNLFNBQVMsR0FBRyxPQUFPLENBQUMsaUNBQWlDLENBQUMsQ0FBQztBQUU3RCxNQUFNLDZCQUE2QixHQUFHLEdBQUcsQ0FBQztBQUMxQyxNQUFNLHFCQUFxQixHQUFHLE1BQU0sQ0FBQyxPQUFPLENBQUMsR0FBRyxDQUFDLHFCQUFxQixJQUFJLDZCQUE2QixDQUFDLENBQUM7QUFJekcsTUFBTSxPQUFPLFVBQVU7SUFBdkI7UUFFRSxTQUFJLEdBQVcsT0FBTyxDQUFDLEdBQUcsQ0FBQyxLQUFLLElBQUksT0FBTyxDQUFDLEdBQUcsQ0FBQyxRQUFRLElBQUksS0FBSyxDQUFDO1FBQ2xFLG9CQUFlLEdBQUcsT0FBTyxDQUFDLEdBQUcsQ0FBQyxpQkFBaUIsQ0FBQztRQUNoRCxlQUFVLEdBQUcsT0FBTyxDQUFDLEdBQUcsQ0FBQyxxQkFBcUIsQ0FBQztRQUMvQyxlQUFVLEdBQUcsT0FBTyxDQUFDLEdBQUcsQ0FBQyxrQkFBa0IsSUFBSSxXQUFXLENBQUM7UUFDM0QsV0FBTSxHQUFHLE9BQU8sQ0FBQyxHQUFHLENBQUMsU0FBUyxJQUFJLE9BQU8sQ0FBQztRQUMxQyxnQkFBVyxHQUFVLEVBQUUsQ0FBQztRQUV4QixpQkFBWSxHQUFHLEdBQUcsRUFBRTtZQUNsQixJQUFJLElBQUksQ0FBQyxlQUFlLElBQUksSUFBSSxDQUFDLFVBQVUsRUFBRTtnQkFDM0MsT0FBTztvQkFDTCxXQUFXLEVBQUUsSUFBSSxDQUFDLGVBQWU7b0JBQ2pDLGVBQWUsRUFBRSxJQUFJLENBQUMsVUFBVTtvQkFDaEMsTUFBTSxFQUFFLElBQUksQ0FBQyxVQUFVO2lCQUN4QixDQUFDO2FBQ0g7WUFDRCxPQUFPO2dCQUNMLE1BQU0sRUFBRSxJQUFJLENBQUMsVUFBVTthQUN4QixDQUFDO1FBQ0osQ0FBQyxDQUFDO1FBTUYscUJBQWdCLEdBQUcsQ0FBQyxPQUFlLEVBQUUsRUFBRTtZQUNyQyxPQUFPLElBQUksU0FBUyxDQUFDO2dCQUNuQixVQUFVLEVBQUUsT0FBTztnQkFDbkIsS0FBSyxFQUFFLE9BQU87YUFDZixDQUFDLENBQUM7UUFDTCxDQUFDLENBQUM7UUFFRix1QkFBa0IsR0FBRyxHQUFHLEVBQUU7WUFDeEIsT0FBTyxJQUFJLE9BQU8sQ0FBQyxVQUFVLENBQUMsT0FBTyxDQUFDO2dCQUNwQyxLQUFLLEVBQUUsSUFBSSxDQUFDLE1BQU07YUFDbkIsQ0FBQyxDQUFDO1FBQ0wsQ0FBQyxDQUFDO1FBa0NGLDBCQUFxQixHQUFHLENBQUMsU0FBaUIsRUFBRSxVQUFtQixFQUFFLEVBQUU7WUFDakUsSUFBSSxJQUFJLENBQUMsUUFBUSxFQUFFLEVBQUU7Z0JBQ25CLElBQUksQ0FBQyxXQUFXLENBQUMsSUFBSSxDQUNuQixJQUFJLG1CQUFtQixDQUFDO29CQUN0QixZQUFZLEVBQUUsU0FBUztvQkFDdkIsYUFBYSxFQUFFLFVBQVU7b0JBQ3pCLGNBQWMsRUFBRSxJQUFJO29CQUNwQixlQUFlLEVBQUUsSUFBSTtvQkFDckIsa0JBQWtCLEVBQUUsSUFBSTtvQkFDeEIsb0JBQW9CLEVBQUUsQ0FBQztvQkFDdkIsU0FBUyxFQUFFLHFCQUFxQjtvQkFDaEMsU0FBUyxFQUFFLElBQUksQ0FBQyxZQUFZLEVBQUU7aUJBQy9CLENBQUMsQ0FDSCxDQUFDO2FBQ0g7UUFDSCxDQUFDLENBQUM7SUF3RkosQ0FBQztJQXhKUyxlQUFlO1FBQ3JCLE9BQU8sQ0FBQyxDQUFDLE9BQU8sQ0FBQyxHQUFHLENBQUMsZUFBZSxDQUFDO0lBQ3ZDLENBQUM7SUFlRCxVQUFVLENBQUMsR0FBVztRQUNwQixPQUFPLEdBQUc7YUFDUCxXQUFXLEVBQUU7YUFDYixPQUFPLENBQUMsY0FBYyxFQUFFLEVBQUUsQ0FBQzthQUMzQixPQUFPLENBQUMsU0FBUyxFQUFFLEVBQUUsQ0FBQyxDQUFDO0lBQzVCLENBQUM7SUFFRCxpQkFBaUIsQ0FBQyxRQUFpQjtRQUNqQyxNQUFNLE9BQU8sR0FBRyxPQUFPLENBQUMsR0FBRyxDQUFDLGdCQUFnQixDQUFDO1FBQzdDLElBQUksU0FBUyxDQUFDO1FBQ2QsSUFBSSxRQUFRLEdBQUcsRUFBRSxDQUFDO1FBQ2xCLElBQUksT0FBTyxFQUFFO1lBQ1gsUUFBUSxHQUFHLEdBQUcsSUFBSSxDQUFDLFVBQVUsQ0FBQyxPQUFPLENBQUMsRUFBRSxDQUFDO1NBQzFDO1FBQ0QsU0FBUyxHQUFHLEdBQUcsSUFBSSxDQUFDLElBQUksSUFBSSxRQUFRLEVBQUUsQ0FBQztRQUN2QyxJQUFJLFFBQVEsRUFBRTtZQUNaLFNBQVMsR0FBRyxHQUFHLFNBQVMsSUFBSSxRQUFRLEVBQUUsQ0FBQztTQUN4QztRQUNELE9BQU8sU0FBUyxDQUFDO0lBQ25CLENBQUM7SUFFRCxlQUFlLENBQUMsSUFBYTtRQUMzQixJQUFJLFVBQVUsR0FBRyxHQUFHLE1BQU0sQ0FBQyxJQUFJLElBQUksRUFBRSxFQUFFLFdBQVcsQ0FBQyxJQUFJLE1BQU0sRUFBRSxFQUFFLENBQUM7UUFDbEUsSUFBSSxJQUFJLEVBQUU7WUFDUixVQUFVLEdBQUcsSUFBSSxDQUFDLFVBQVUsQ0FBQyxJQUFJLENBQUMsQ0FBQztTQUNwQztRQUNELElBQUksT0FBTyxDQUFDLEdBQUcsQ0FBQyxpQ0FBaUMsRUFBRTtZQUNqRCxVQUFVLEdBQUcsTUFBTSxFQUFFLENBQUM7U0FDdkI7UUFDRCxPQUFPLFVBQVUsQ0FBQztJQUNwQixDQUFDO0lBbUJELGFBQWEsQ0FBQyxTQUFpQixFQUFFLFVBQWtCLEVBQUUsTUFBZTtRQUNsRSxJQUFJLFFBQVEsR0FBRyxRQUFRLFNBQVMsQ0FBQyxPQUFPLENBQUMsR0FBRyxFQUFFLEdBQUcsQ0FBQyxFQUFFLENBQUM7UUFDckQsSUFBSSxVQUFVLEVBQUU7WUFDZCxRQUFRLEdBQUcsR0FBRyxRQUFRLElBQUksVUFBVSxDQUFDLE9BQU8sQ0FBQyxHQUFHLEVBQUUsR0FBRyxDQUFDLEVBQUUsQ0FBQztTQUMxRDtRQUVELE9BQU8sR0FBRyxRQUFRLElBQUksTUFBTSxJQUFJLE9BQU8sTUFBTSxDQUFDO0lBQ2hELENBQUM7SUFFRCxRQUFRO1FBQ04sT0FBTyxDQUFDLENBQUMsT0FBTyxNQUFNLElBQUksV0FBVyxJQUFJLE1BQU0sQ0FBQyxRQUFRLENBQUMsQ0FBQztJQUM1RCxDQUFDO0lBRUQsa0JBQWtCLENBQUMsU0FBaUIsRUFBRSxVQUFrQjtRQUN0RCxJQUFJLElBQUksQ0FBQyxRQUFRLEVBQUUsRUFBRTtZQUNuQixJQUFJLENBQUMsV0FBVyxDQUFDLElBQUksQ0FDbkIsSUFBSSxPQUFPLENBQUMsVUFBVSxDQUFDLElBQUksQ0FBQztnQkFDMUIsUUFBUSxFQUFFLElBQUksQ0FBQyxhQUFhLENBQUMsU0FBUyxFQUFFLFVBQVUsQ0FBQztnQkFDbkQsT0FBTyxFQUFFLE9BQU87Z0JBQ2hCLGdCQUFnQixFQUFFLElBQUk7YUFDdkIsQ0FBQyxDQUNILENBQUM7U0FDSDtJQUNILENBQUM7SUFFRCx1QkFBdUIsQ0FBQyxTQUFpQixFQUFFLFVBQWtCO1FBQzNELElBQUksSUFBSSxDQUFDLFFBQVEsRUFBRSxFQUFFO1lBQ25CLE9BQU87Z0JBQ0wsSUFBSSxPQUFPLENBQUMsVUFBVSxDQUFDLElBQUksQ0FBQztvQkFDMUIsUUFBUSxFQUFFLElBQUksQ0FBQyxhQUFhLENBQUMsU0FBUyxFQUFFLFVBQVUsRUFBRSxRQUFRLENBQUM7b0JBQzdELE9BQU8sRUFBRSxPQUFPO2lCQUNqQixDQUFDO2FBQ0gsQ0FBQztTQUNIO1FBQ0QsT0FBTyxTQUFTLENBQUM7SUFDbkIsQ0FBQztJQUVELGtCQUFrQjtRQUNoQixPQUFPO1lBQ0wsTUFBTSxFQUFFLE9BQU8sQ0FBQyxNQUFNLENBQUMsT0FBTyxDQUM1QixPQUFPLENBQUMsTUFBTSxDQUFDLElBQUksRUFBRSxFQUNyQixJQUFJLGNBQWMsQ0FBQztnQkFDakIsVUFBVSxFQUFFLElBQUk7Z0JBQ2hCLFdBQVcsRUFBRSxJQUFJO2FBQ2xCLENBQUMsRUFDRixPQUFPLENBQUMsTUFBTSxDQUFDLEdBQUcsRUFBRSxDQUNyQjtTQUNGLENBQUM7SUFDSixDQUFDO0lBRUQsTUFBTSxDQUFDLFNBQWtCLEVBQUUsUUFBaUI7UUFDMUMsTUFBTSxTQUFTLEdBQUcsSUFBSSxDQUFDLGlCQUFpQixDQUFDLFFBQVEsQ0FBQyxDQUFDO1FBQ25ELE1BQU0sVUFBVSxHQUFHLElBQUksQ0FBQyxlQUFlLENBQUMsU0FBUyxDQUFDLENBQUM7UUFDbkQsTUFBTSxNQUFNLEdBQUcsR0FBRyxTQUFTLElBQUksVUFBVSxFQUFFLENBQUM7UUFDNUMsSUFBSSxNQUFNLEdBQUcsVUFBVSxDQUFDLE9BQU8sQ0FBQyxNQUFNLENBQUMsQ0FBQztRQUN4QyxJQUFJLE1BQU0sRUFBRTtZQUNWLE9BQU8sTUFBTSxDQUFDO1NBQ2Y7UUFFRCxJQUFJLGlCQUFpQixFQUFFO1lBQ3JCLElBQUksQ0FBQyxXQUFXLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxnQkFBZ0IsQ0FBQyxpQkFBaUIsQ0FBQyxDQUFDLENBQUM7U0FDakU7UUFFRCxJQUFJLElBQUksQ0FBQyxlQUFlLEVBQUUsRUFBRTtZQUMxQixJQUFJLENBQUMscUJBQXFCLENBQUMsU0FBUyxFQUFFLFVBQVUsQ0FBQyxDQUFDO1lBQ2xELE1BQU0sR0FBRyxPQUFPLENBQUMsWUFBWSxDQUFDO2dCQUM1QixLQUFLLEVBQUUsSUFBSSxDQUFDLE1BQU07Z0JBQ2xCLE1BQU0sRUFBRSxJQUFJLG1CQUFtQixDQUFDO29CQUM5QixXQUFXLEVBQUUsS0FBSztpQkFDbkIsQ0FBQztnQkFDRixVQUFVLEVBQUUsSUFBSSxDQUFDLFdBQVc7YUFDN0IsQ0FBQyxDQUFDO1NBQ0o7YUFBTTtZQUNMLElBQUksQ0FBQyxXQUFXLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxrQkFBa0IsRUFBRSxDQUFDLENBQUM7WUFDakQsSUFBSSxDQUFDLGtCQUFrQixDQUFDLFNBQVMsRUFBRSxVQUFVLENBQUMsQ0FBQztZQUMvQyxNQUFNLEdBQUcsT0FBTyxDQUFDLFlBQVksQ0FBQztnQkFDNUIsTUFBTSxFQUFFLFVBQVU7Z0JBQ2xCLEtBQUssRUFBRSxJQUFJLENBQUMsTUFBTTtnQkFDbEIsR0FBRyxJQUFJLENBQUMsa0JBQWtCLEVBQUU7Z0JBQzVCLFVBQVUsRUFBRSxJQUFJLENBQUMsV0FBVztnQkFDNUIsaUJBQWlCLEVBQUUsSUFBSSxDQUFDLHVCQUF1QixDQUFDLFNBQVMsRUFBRSxVQUFVLENBQUM7YUFDdkUsQ0FBQyxDQUFDO1NBQ0o7UUFDRCxVQUFVLENBQUMsT0FBTyxDQUFDLE1BQU0sQ0FBQyxHQUFHLE1BQU0sQ0FBQztRQUNwQyxPQUFPLE1BQU0sQ0FBQztJQUNoQixDQUFDOztBQTVLTSxrQkFBTyxHQUFtQyxFQUFFLENBQUMifQ==